import * as dateFnsCompat from 'utils/date-fns-compat';

import he from 'he';
import fp from 'lodash/fp';
import React, { Component } from 'react';
import { Button, Col, Modal, Row } from 'react-bootstrap';
import { FiCamera, FiX } from 'react-icons/fi';
import { connect } from 'react-redux';
import Spinner from 'react-spinkit';
import { bindActionCreators } from 'redux';
import { genders, months } from '../../../../../constants';
import { handleInvalidForm } from '../../../../../services';
import { geonamesActions } from '../../../../../state/modules/geonames';
import { currentUserSelector, userActions } from '../../../../../state/modules/user';
import { noCapMap } from '../../../../../utils';
import { ControlGroup, ControlOption, FileUploadFormField, FormField, FormGroup, FormSubmissionMessage, getInitials, Icon, ImageCropField, InputFormField, TypeaheadField } from '../../../../components';
import moment from 'moment-timezone';

class Personal extends Component {
  constructor() {
    super();

    this.state = {
      details: {
        firstName: '',
        lastName: '',
        phone: '',
        suburb: '',
        state: '',
        postcode: '',
        dateOfBirth: null,
        gender: '',
        timezoneName: ''
      },
      avatar: undefined,
      avatarUrl: undefined,
      croppedAvatarUrl: undefined,
      showingCroppingModal: false,
      validation: {
        firstName: true,
        lastName: true,
        dateOfBirth: true
      },
      isSubmitting: false,
      showSubmissionMessage: false,
      focusMessage: false
    };
  }

  componentDidMount() {
    // If the currentUser is already in state, then let's grab it
    this.updateUserDetails(this.props.user);
  }

  componentWillReceiveProps(nextProps) {
    const {
      isUpdating,
      user: nextUser
    } = nextProps;

    const {
      isUpdating: wasUpdating,
      user: previousUser
    } = this.props;

    // If the component mounted, but the currentUser wasn't in the state already,
    // then grab it when it finishes fetching.
    // (Ignore any updates if we already had the currentUser in componentDidMount)
    if (!previousUser.id && nextUser.id) {
      this.updateUserDetails(nextUser);
    }

    if (wasUpdating && !isUpdating) {
      this.setState({
        isSubmitting: false,
        showSubmissionMessage: true,
        focusMessage: true
      });
    }
  }

  updateUserDetails(user) {
    this.setState(({ details }) => ({
      details: {
        ...details,
        ...fp.pick(fp.keys(details))(user)
      },
      avatarUrl: user.avatarUrl
    }));
  }

  onFormChange(event) {
    const newValue = { [event.target.name]: event.target.value };

    this.setState(({ details }) => ({
      details: {
        ...details,
        ...newValue,
        suburb: this.state.suburbSearchText ? this.state.suburbSearchText : details.suburb
      }
    }));
  }

  onAvatarDrop = (acceptedFiles) => {
    const acceptedFile = fp.first(acceptedFiles);
    if (acceptedFile) {
      this.setState({
        avatar: acceptedFile,
        showingCroppingModal: true
      })
    }
  }

  onAvatarCropped = (blob) => this.setState({
    avatar: blob,
    croppedAvatarUrl: window.URL.createObjectURL(blob),
    showingCroppingModal: false
  })

  hideCroppingModal = () => {
    this.setState({
      showingCroppingModal: false
    })
  }

  cancelCropping = () => {
    this.setState({
      avatar: undefined,
      showingCroppingModal: false
    })
  }

  removeAvatar = () => {
    this.setState({
      avatar: undefined,
      avatarUrl: undefined,
      croppedAvatarUrl: undefined
    })
  }

  currentYear = () => dateFnsCompat.getYear(new Date());

  onDateFieldChange(transform, value) {
    const parsedValue = parseInt(value, 10);

    this.setState(({ details }) => ({
      details: {
        ...details,
        dateOfBirth: !isNaN(parsedValue) ?
          transform(details.dateOfBirth || new Date(this.currentYear(), 0, 1), parsedValue) :
          null
      }
    }));
  }

  onFormSubmit(event) {
    event.preventDefault();
    event.stopPropagation();

    const {
      updateUser,
      user
    } = this.props;

    const {
      avatar,
      details,
      isSubmitting,
      suburbSearchText
    } = this.state;

    if (!isSubmitting) {
      if (this.validateForm()) {
        this.setState({ isSubmitting: true });
        let userDetailsToUpdate = details;

        // If the suburb search text is set, that means user did not select entry from typeahead.
        if (suburbSearchText) {
          userDetailsToUpdate = {
            ...userDetailsToUpdate,
            suburb: suburbSearchText
          };

          this.setState(({ details }) => ({
            suburbSearchText: null,
            details: {
              ...details,
              suburb: suburbSearchText
            }
          }));
        }

        updateUser({
          userDetails: {
            ...user,
            ...userDetailsToUpdate,
            avatar: avatar ? user.avatar : null
          },
          avatar
        });
      } else {
        this.setState({ showSubmissionMessage: false });

        if (this.formRef) {
          handleInvalidForm(this.formRef);
        }
      }
    }
  }

  openUploadDialog = () => {
    if (this.dropzoneRef) {
      this.dropzoneRef.open()
    }
  }

  validateForm() {
    const {
      details: {
        firstName,
        lastName,
        dateOfBirth
      }
    } = this.state;

    const validation = {
      firstName: firstName.trim() !== '',
      lastName: lastName.trim() !== '',
      dateOfBirth: dateFnsCompat.isPast(dateOfBirth)
    };

    this.setState({ validation });

    return fp.all(fp.identity)(validation);
  }

  renderSuburbResult(option, props, index) {
    return he.decode(`${option.placeName} - ${option.postalCode}`);
  }

  onSuburbTextChange(value) {
    this.props.searchSuburbs({ placename: value });
  }

  onSuburbInputChange(value) {
    this.setState({ suburbSearchText: value });
    if (fp.isEmpty(value)) {
      this.setState(({ details }) => ({
        details: {
          ...details,
          suburb: ''
        }
      }));
    }
  }

  onSuburbSelected(suburbs) {
    const suburb = fp.first(suburbs);
    if (suburb) {
      this.setState(({ details }) => ({
        suburbSearchText: null,
        details: {
          ...details,
          suburb: suburb.placeName,
          postcode: suburb.postalCode,
          state: suburb['ISO3166-2']
        }
      }));
    }
  }

  render() {
    const {
      error
    } = this.props;

    const {
      details: {
        firstName,
        lastName,
        phone,
        state,
        suburb,
        postcode,
        dateOfBirth,
        gender,
        timezoneName
      },
      validation,
      isSubmitting,
      showSubmissionMessage
    } = this.state;

    const birthYear = dateOfBirth ? dateFnsCompat.getYear(dateOfBirth) : '';
    const birthMonth = dateOfBirth ? dateFnsCompat.getMonth(dateOfBirth) : '';
    const birthDate = dateOfBirth ? dateFnsCompat.getDate(dateOfBirth) : '';
    const timezoneNames = moment.tz.names();

    return (
      <form
        ref={(form) => { this.formRef = form; }}
        noValidate
        onSubmit={(e) => this.onFormSubmit(e)}>
        <FormSubmissionMessage
          className='personal-details-message'
          showSubmissionMessage={showSubmissionMessage}
          submissionMessage={'Your details were successfully updated'}
          error={error}
          stopFocusingMessage={() => this.setState({ focusMessage: false })}
          hideSubmissionMessage={() => this.setState({ showSubmissionMessage: false })} />
        <Row>
          <Col xs={12}>
            <fieldset>
              <p className='personal-details'>
                <strong>Personal details</strong>
              </p>
              <div>
                <FileUploadFormField
                  inputRef={(el) => { this.dropzoneRef = el }}
                  className='hidden'
                  styleName='file-6'
                  multiple={false}
                  accept='image/png,image/jpeg'
                  onFileDrop={this.onAvatarDrop}
                  file={this.state.avatar}
                  dropLabel='Upload Image (JPG, PNG)'
                />
              </div>
              <Row>
                <Col xs={12} md={2}>
                  {
                    this.state.avatarUrl || this.state.croppedAvatarUrl ?
                      <div className='avatar-wrapper'>
                        <div>
                          <img
                            alt={[this.props.user.firstName, this.props.user.lastName].join(' ')}
                            src={he.decode(this.state.croppedAvatarUrl ? this.state.croppedAvatarUrl : this.state.avatarUrl)} />
                        </div>
                        <div className='edit-overlay'>
                          <span><FiCamera onClick={this.openUploadDialog} /></span>
                          <FiX onClick={this.removeAvatar} />
                        </div>
                      </div> : null
                  }
                  {
                    !this.state.avatarUrl && !this.state.croppedAvatarUrl ?
                      <div className='avatar-wrapper'>
                        <div className='initialAvatar'>
                          <span>
                            {getInitials([this.props.user.firstName, this.props.user.lastName].join(' '))}
                          </span>
                        </div>
                        <div className='edit-overlay single-icon'>
                          <FiCamera onClick={this.openUploadDialog} />
                        </div>
                      </div> : null
                  }
                </Col>
                <Col xs={12} md={10}>
                  <Row>
                    <Col xs={12} md={6}>
                      <InputFormField
                        id='first-name'
                        required
                        validation={validation.firstName ? null : 'First name is required.'}
                        value={he.decode(firstName)}
                        onChange={(e) => this.onFormChange(e)} />
                    </Col>
                    <Col xs={12} md={6}>
                      <InputFormField
                        id='last-name'
                        required
                        validation={validation.lastName ? null : 'Last name is required.'}
                        value={he.decode(lastName)}
                        onChange={(e) => this.onFormChange(e)} />
                    </Col>
                  </Row>
                  <Row>
                    <Col xs={12} md={6}>
                      <InputFormField
                        id='phone'
                        inputType='number'
                        value={phone}
                        onChange={(e) => this.onFormChange(e)} />
                    </Col>
                    <Col xs={12} md={6}>
                      <TypeaheadField
                        label='Suburb'
                        searchText={suburb || this.state.suburbSearchText}
                        isLoading={this.props.geonames.isFetching}
                        emptyLabel={this.props.geonames.isFetching ? 'Searching...' : 'No matches'}
                        options={this.props.geonames ? this.props.geonames.suburbs : []}
                        labelKey={(o) => he.decode(o.placeName)}
                        renderMenuItemChildren={this.renderSuburbResult}
                        onSearch={(v) => this.onSuburbTextChange(v)}
                        onChange={(v) => this.onSuburbSelected(v)}
                        onInputChange={(v) => this.onSuburbInputChange(v)}
                        selected={[{ placeName: suburb, postalCode: postcode, 'ISO3166-2': state }]}
                      />
                    </Col>
                  </Row>
                  <Row>
                    <Col xs={12} md={6}>
                      <InputFormField
                        id='state'
                        value={state}
                        onChange={(e) => this.onFormChange(e)} />
                    </Col>
                    <Col xs={12} md={6}>
                      <InputFormField
                        id='postcode'
                        inputType='number'
                        value={postcode}
                        onChange={(e) => this.onFormChange(e)} />
                    </Col>
                  </Row>
                  <Row>
                    <Col xs={12} md={6}>
                      <FormGroup
                        id='date-of-birth'
                        validation={validation.dateOfBirth ? null : 'Date of birth must be in the past.'}>
                        <Row>
                          <Col xs={12}>
                            <h4 className='small'>Date of birth</h4>
                          </Col>
                        </Row>
                        <Row className='xs-gutter'>
                          <Col xs={12} sm={4}>
                            <FormField id='date' label='Day' type='select-item'>
                              <select
                                id='date'
                                value={birthDate}
                                onChange={(e) => this.onDateFieldChange(dateFnsCompat.setDate, e.target.value)}>
                                <option value=''></option>
                                {
                                  fp.map((day) =>
                                    <option key={day} value={day}>{day}</option>
                                  )(fp.range(1, (dateOfBirth ? dateFnsCompat.getDaysInMonth(dateOfBirth) : 31) + 1))
                                }
                              </select>
                            </FormField>
                          </Col>
                          <Col xs={12} sm={4}>
                            <FormField id='month' type='select-item'>
                              <select
                                id='month'
                                value={birthMonth}
                                onChange={(e) => this.onDateFieldChange(dateFnsCompat.setMonth, e.target.value)}>
                                <option value=''></option>
                                {
                                  noCapMap((month, index) =>
                                    <option key={index} value={index}>{month}</option>
                                  )(fp.values(months))
                                }
                              </select>
                            </FormField>
                          </Col>
                          <Col xs={12} sm={4}>
                            <FormField id='year' type='select-item'>
                              <select
                                id='year'
                                value={birthYear}
                                onChange={(e) => this.onDateFieldChange(dateFnsCompat.setYear, e.target.value)}>
                                <option value=''></option>
                                {
                                  fp.map((year) =>
                                    <option key={year} value={year}>{year}</option>
                                  )(fp.rangeRight(1900, this.currentYear() + 1))
                                }
                              </select>
                            </FormField>
                          </Col>
                        </Row>
                        <Row>
                          <Col xs={12}>
                            <FormField id='timezone' type='select-item'>
                              <select
                                id='timezoneName'
                                name='timezoneName'
                                value={timezoneName}
                                onChange={(e) => this.onFormChange(e)}>
                                {
                                  timezoneNames.map((name) => <option key={name} value={name}>{name}</option>)
                                }
                              </select>
                            </FormField>
                          </Col>
                        </Row>
                      </FormGroup>
                    </Col>
                    <Col xs={12} md={6}>
                      <ControlGroup
                        id='gender'
                        control='radio'
                        type='pill'
                        formItemProps={{
                          className: 'pill'
                        }}
                        labelProps={{
                          className: 'small'
                        }}
                        onChange={(e) => this.onFormChange(e)}>
                        {
                          fp.map(({ label, value }) =>
                            <ControlOption key={value} value={value} checked={gender === value} label={<span>{label}</span>} />
                          )(fp.values(genders))
                        }
                      </ControlGroup>
                    </Col>
                  </Row>
                </Col>
              </Row>
              <div className='gap' />
              <Col xs={12} md={6} mdOffset={3}>
                <Button type='submit' className='large' disabled={isSubmitting}>
                  {
                    isSubmitting ?
                      <Spinner name='circle' className='spinner' fadeIn='none' /> :
                      'Update details'
                  }
                </Button>
              </Col>
            </fieldset>
          </Col>
          <Modal show={this.state.showingCroppingModal} onHide={this.hideCroppingModal} dialogClassName='image-cropping-modal'>
            <Modal.Body>
              <ImageCropField
                className='question-image'
                imageSrc={this.state.avatar ? this.state.avatar.preview : null}
                onComplete={this.onAvatarCropped}
                shape='circle' />
              <button aria-label='Remove Image' className='btn remove-cropped-question-image' onClick={this.cancelCropping}>
                <Icon icon='close' />
              </button>
            </Modal.Body>
          </Modal>
        </Row>
      </form>
    )
  }
};

const mapStateToProps = (state) => ({
  user: currentUserSelector(state),
  geonames: state.geonames,
  isUpdating: state.user.isUpdating,
  error: state.user.error
});

const mapDispatchToProps = (dispatch) => ({
  updateUser: bindActionCreators(userActions.user.update, dispatch),
  searchSuburbs: bindActionCreators(geonamesActions.geonames.suburbs.get, dispatch),
});

export default connect(mapStateToProps, mapDispatchToProps)(Personal);



// WEBPACK FOOTER //
// ./src/containers/pages/app/account-settings/partials/personal.js